import { useAtom } from '@reatom/npm-react'
import { AnimatePresence, motion } from 'motion/react'
import styled from 'styled-components'

import { useHapticFeedback } from '@blade/ui-kit'

import { WalletAssetListItem } from '../../wallet-asset/mobile/wallet-asset-list-item'

import { WalletListItemCard } from './wallet-list-item-card'

import type { WalletAssetModel } from '@root/entities/wallet/atoms'
import type { WalletModel } from '@root/entities/wallet/atoms/reatom-wallet'

export type WalletListItemProps = {
  expanded?: boolean
  active?: boolean
  wallet: WalletModel
  onClick?: React.MouseEventHandler<HTMLElement>
  onAssetClick: (asset: WalletAssetModel) => void
  variant?: 'default' | 'radio'
  onDetailsClick?: React.MouseEventHandler<HTMLElement>
  onSelected?: (wallet: WalletModel) => void
  renderFooter?: () => React.ReactNode
}

export const WalletListItem: React.FC<WalletListItemProps> = ({
  expanded,
  active = false,
  wallet,
  variant = 'default',
  onClick,
  onDetailsClick,
  onAssetClick,
  onSelected,
  renderFooter,
}) => {
  const haptics = useHapticFeedback()

  const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!onClick) {
      return
    }

    if (variant === 'radio') {
      onSelected?.(wallet)
      return
    }

    haptics.impactOccurred('light')
    onClick(e)
  }

  const handleArrowClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!onClick) {
      return
    }

    e.stopPropagation()
    haptics.impactOccurred('light')
    onClick(e)
  }

  const handleSelected = () => {
    onSelected?.(wallet)
  }

  const [assets] = useAtom(wallet.assets)

  return (
    <Root
      $active={active}
      $variant={variant}
      $clickable={!!onClick}
      layout
      layoutRoot
      onClick={handleClick}
    >
      <WalletListItemCard
        variant={variant}
        wallet={wallet}
        onDetailsClick={onDetailsClick}
        onSelected={handleSelected}
        selected={active}
        onArrowClick={handleArrowClick}
        renderFooter={renderFooter}
      />
      <AnimatePresence>
        {expanded ? (
          <motion.div
            layout
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: 'auto' },
              collapsed: { opacity: 0, height: 0 },
            }}
          >
            <ExpandedContent>
              {assets.map((asset) => (
                <WalletAssetListItem
                  onClick={
                    asset.pairAddress
                      ? (asset) => {
                          onAssetClick(asset)
                        }
                      : undefined
                  }
                  key={asset.currency.ticker}
                  asset={asset}
                  withAddress={false}
                  variant="compact"
                />
              ))}
            </ExpandedContent>
          </motion.div>
        ) : undefined}
      </AnimatePresence>
    </Root>
  )
}

const Root = styled(motion.div)<{
  $active: boolean
  $variant: 'default' | 'radio'
  $clickable: boolean
}>`
  position: relative;
  border-radius: 8px;
  padding-inline: 10px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'auto')};

  border: ${({ $active, $variant }) =>
    $active
      ? `1px solid ${$variant === 'radio' ? 'var(--primary-9)' : 'var(--primary-7)'}`
      : `1px solid transparent`};
  background: var(--background-2);
`

const ExpandedContent = styled(motion.div)`
  border-top: 1px solid var(--appearanceA-1);
  display: grid;
`
